import { themeGet } from '@styled-system/theme-get';
import React, { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { HybridAvailability, SeriesAvailability } from '../../SeriesAvailability';
import { ReactComponent as GreenAvailabilityIcon } from '../../../../assets/images/AvailabilityIcons/Green.svg';
import { ReactComponent as YellowAvailabilityIcon } from '../../../../assets/images/AvailabilityIcons/Yellow.svg';
import { ReactComponent as RedAvailabilityIcon } from '../../../../assets/images/AvailabilityIcons/Red.svg';
import { Dropdown } from '../../../../components/Dropdown/Dropdown';
import { ISeriesWithHybrid } from '../../../../logic/store/Seeds/SeedsActions';
import { IOptionItem } from '../../../../components/Dropdown/IOptionItem';
import { Checkbox } from '../../../../components/Checkbox/Checkbox';
import { ICropPlanSelectedProduct } from '../../../../logic/Models/Responses/FieldPlanResponse';
import { useCropPlanTrackingState } from '../useCropPlanTrackingState';
import { useAmplitudePassthrough } from '../../../../logic/Utility/useAmplitude';
import { useScopedSession } from '../../../../tracing/session';
import { EventStructureTags } from '../../../../tracing/EventTagNames';
import { EventFieldPlanNames } from '../../../../tracing/EventNames';
import { HybridAvailability7, TreatmentAvailability7 } from '../../../../logic/Models/Responses/SeedResponseV7';
import { autoSelectTreatmentForHybrid } from '../../../../logic/store/Seeds/SeedLookups';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../logic/store/Store';
import { ReactComponent as AlertIcon } from '../../Icons/AlertIcon.svg';
import { Popover } from 'antd';

const SeriesRow = styled.div`
	display: flex;
	flex-direction: row;
	width: 100%;
	height: 80px;
	min-height: 80px;
	background-color: ${themeGet('colors.white')};
	border: 1px solid ${themeGet('colors.lightestGrey')};
	border-radius: 5px;
	margin-bottom: 8px;
	align-items: center;
	box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.15);
`;

const SeriesData = styled.div`
	color: ${themeGet('colors.darkGrey')};
	font-family: ${themeGet('fonts.base')};
	font-size: ${themeGet('fontSizes.extraSmall')};
	font-weight: ${themeGet('fontWeights.bold')};
	padding: 0px 12px;
	padding-left: 15px;
	width: 100%;
`;

export interface ISeriesWithHybridRowProps
{
	selected: boolean; // Whether the series is selected in the list
	series: ISeriesWithHybrid; // The series that this row represents
	selectedHybrids: ICropPlanSelectedProduct[];
	selectHybridForSeries: (seriesId: string, hybridId: string, treatment: string, treatmentId: string) => void;
	selectSingleSeries: (selected: boolean, seriesId: string, hybridId: string, treatment: string, treatmentId: string) => void;
	isGoldenHarvestUser: boolean;
}

/**
 * The Series With Hybrid/Treatment list dropdown and checkbox
 */
export const SeriesWithHybridRow = (props: ISeriesWithHybridRowProps) =>
{
	const {
		selected,
		series,
		selectedHybrids,
		selectHybridForSeries,
		selectSingleSeries,
		isGoldenHarvestUser
	} = props;

	const theme = useTheme();

	const brandLookup = useSelector((state: RootState) => state.seeds.brands)!;
	const hybridLookup = useSelector((state: RootState) => state.seeds.hybrids)!;

	const [hybridOptions, setHybridOptions] = useState<IOptionItem[]>([]);
	const [selectedHybridId, setSelectedHybridId] = useState<string>(undefined);
	const hybrid = series.hybrids.find(h => h.HybridId === selectedHybridId);

	const [treatmentOptions, setTreatmentOptions] = useState<IOptionItem[]>([]);
	const [selectedTreatmentId, setSelectedTreatmentId] = useState<string>(undefined);
	const [hybridTreatmentError, setHybridTreatmentError] = useState<boolean>(false);

	const trackingData = useCropPlanTrackingState();
	const session = useScopedSession(SeriesWithHybridRow.name, trackingData, {
		[EventStructureTags.PageContext]: 'series_with_hybrid_row',
		[EventStructureTags.PageUrl]: window.location.toString()
	});

	const getTreatmentOptions = (hybrid: HybridAvailability7) =>
	{
		if (hybrid)
		{
			const filteredTreatments = Object.values(hybrid.Treatments).filter(h => h.Availability!== 'None' && h.Availability !== 'Unknown');
			const createTreatmentOptions = filteredTreatments.map(treatment =>
			{
				const treatmentOption: IOptionItem =
				{
					// Add the availability icon
					icon: <HybridAvailability style={{width: 14, marginRight: 6}}>
						{treatment.Availability == 'Green' ? <GreenAvailabilityIcon style={{ fill: theme.colors.availabilityGreen }} />
							: treatment.Availability === 'Yellow' ? <YellowAvailabilityIcon style={{ fill: theme.colors.availabilityYellow }} />
								: <RedAvailabilityIcon style={{ fill: theme.colors.availabilityRed }} />
						}
					</HybridAvailability>,
					label: treatment.TreatmentName,
					value: treatment.TreatmentId,
				};

				return treatmentOption;
			});

			setTreatmentOptions(createTreatmentOptions);
		}
	};

	const selectHybrid = useAmplitudePassthrough(session, EventFieldPlanNames.ChangeRequestedTrait, (hybridId: string) =>
	{
		const foundHybrid = series.hybrids.find(h => h.HybridId === hybridId);
		const carriedOverTreatment = selectedTreatmentId ? foundHybrid.Treatments[selectedTreatmentId] : undefined;
		const defaultTreatment = carriedOverTreatment
			? (carriedOverTreatment.Availability !== 'None' && carriedOverTreatment.Availability !== 'Unknown' ? carriedOverTreatment : undefined)
			: undefined;
		const resolvedTreatment = defaultTreatment ?? autoSelectTreatmentForHybrid(foundHybrid, hybridLookup, brandLookup);

		setSelectedHybridId(hybridId);
		setSelectedTreatmentId(resolvedTreatment?.TreatmentId);
		setHybridTreatmentError(false);
		selectHybridForSeries(series.seriesId, hybridId, resolvedTreatment?.TreatmentName, resolvedTreatment?.TreatmentId);
		getTreatmentOptions(series.hybrids.find(h => h.HybridId === hybridId));
	}, [series, selectedTreatmentId, setSelectedHybridId, selectHybridForSeries, getTreatmentOptions, autoSelectTreatmentForHybrid]);

	const selectTreatment = useAmplitudePassthrough(session, EventFieldPlanNames.ChangeRequestedTreatmentForTrait, (treatmentId: string) =>
	{
		const treatment = series.hybrids.find(h => h.HybridId === selectedHybridId)?.Treatments[treatmentId];
		setSelectedTreatmentId(treatmentId);
		selectHybridForSeries(series.seriesId, selectedHybridId, treatment?.TreatmentName, treatmentId);
	}, [series, selectedHybridId, setSelectedTreatmentId, selectHybridForSeries]);

	const popOverContent = (
		<div style={{ fontWeight: theme.fontWeights.bold }}>
			The previously saved treatment is no longer available, it has been defaulted to the next available option.
		</div>
	);

	/**
	 * Validates if the selected treatment is still orderable. Applies the default if not.
	 *
	 * Treatment availability is dynamic and backend-driven (Green/Yellow/Red/None/Unknown).
	 * A treatment that was valid when a grower saved their crop plan may become unavailable
	 * over time due to seasonal supply changes or regulatory removal.
	 */
	const validateAndApplySelectedTreatment = (
		selectedTreatmentObject: TreatmentAvailability7 | undefined,
		defaultTreatmentForNewHybrid: TreatmentAvailability7,
		selectedProductTreatmentId: string
	) =>
	{
		const treatmentNotFound = !selectedTreatmentObject;
		const treatmentUnavailable =
			selectedTreatmentObject &&
			(selectedTreatmentObject.Availability === 'None' || selectedTreatmentObject.Availability === 'Unknown');

		if (treatmentNotFound || treatmentUnavailable)
		{
			// The previously selected treatment is either absent from this hybrid or no longer available,
			// so set the error icon and select the default treatment
			setSelectedTreatmentId(defaultTreatmentForNewHybrid?.TreatmentId);
			setHybridTreatmentError(true);
			return;
		}

		// Otherwise set the selected treatment because it is available
		setSelectedTreatmentId(selectedProductTreatmentId);
	};

	const reconcileTreatmentSelection = (
		targetHybrid: HybridAvailability7,
		selectedHybridAsProduct: ICropPlanSelectedProduct | undefined,
		selectedProductTreatmentId: string | undefined,
		defaultTreatment: TreatmentAvailability7,
	) =>
	{
		if (selectedHybridAsProduct && selectedProductTreatmentId)
		{
			// Check if a previously selected treatment is still available for the selected hybrid
			const hybridAllTreatments = Object.values(targetHybrid.Treatments);
			const selectedTreatmentObject = hybridAllTreatments.find(t => t.TreatmentId === selectedProductTreatmentId);
			validateAndApplySelectedTreatment(selectedTreatmentObject, defaultTreatment, selectedProductTreatmentId);
		}
		else
		{
			// Set the defaulted treatment
			setSelectedTreatmentId(
				selectedHybridAsProduct ?
					selectedProductTreatmentId ?? defaultTreatment?.TreatmentId
					: defaultTreatment?.TreatmentId
			);
		}

		// Set the treatment options based on the hybrid selection
		getTreatmentOptions(targetHybrid);
	};

	useEffect(() =>
	{
		if (!series?.hybrids) return;

		const availableHybrids = series.hybrids.filter(h => h.Availability !== 'None' && h.Availability !== 'Unknown');

		if (!availableHybrids.length) return;

		const createHybridOptions = availableHybrids.map(hybrid =>
		{
			const hybridOption: IOptionItem =
				{
					// Add the availability icon
					icon: <HybridAvailability style={{width: 14, marginRight: 6}}>
						{hybrid.Availability == 'Green' ? <GreenAvailabilityIcon style={{ fill: theme.colors.availabilityGreen }} />
							: hybrid.Availability === 'Yellow' ? <YellowAvailabilityIcon style={{ fill: theme.colors.availabilityYellow }} />
								: <RedAvailabilityIcon style={{ fill: theme.colors.availabilityRed }} />
						}
					</HybridAvailability>,
					label: hybrid.HybridName,
					value: hybrid.HybridId
				};

			return hybridOption;
		});

		setHybridOptions(createHybridOptions);

		const defaultHybrid = availableHybrids[0];

		if (!selected)
		{
			if (!selectedHybridId)
			{
				const defaultTreatmentForDefaultHybrid = autoSelectTreatmentForHybrid(
					defaultHybrid,
					hybridLookup,
					brandLookup
				);
				setSelectedHybridId(defaultHybrid.HybridId);
				getTreatmentOptions(defaultHybrid);
				setSelectedTreatmentId(defaultTreatmentForDefaultHybrid?.TreatmentId);
			}
			return;
		}

		/**
		 * If the series is selected then we're either:
		 * - opening a previously created crop plan seed choices
		 * - or the user is selecting hybrids for a crop plan
		 */
		const newSelectedHybrid = series.hybrids.length > 1 ?
			series.hybrids.find(h => selectedHybrids.find(sh => sh.HybridId === h.HybridId)) : undefined;
		const selectedHybridAsProduct = selectedHybrids.find(h => h.HybridId === newSelectedHybrid?.HybridId);
		const selectedProductTreatmentId = selectedHybridAsProduct?.TreatmentId; // The previously selected treatment id

		if (newSelectedHybrid)
		{
			setSelectedHybridId(newSelectedHybrid.HybridId);
			const defaultTreatmentForNewHybrid = autoSelectTreatmentForHybrid(newSelectedHybrid, hybridLookup, brandLookup);

			reconcileTreatmentSelection(
				newSelectedHybrid,
				selectedHybridAsProduct,
				selectedProductTreatmentId,
				defaultTreatmentForNewHybrid,
			);
		}
		else
		{
			let effectiveHybrid = defaultHybrid;
			if (selectedHybridId && hybrid)
			{
				effectiveHybrid = hybrid;
			}
			setSelectedHybridId(effectiveHybrid.HybridId);
			const selectedHybridAsProduct = selectedHybrids.find(h => h.HybridId === effectiveHybrid.HybridId);
			const selectedProductTreatmentId = selectedHybridAsProduct?.TreatmentId;
			const defaultTreatmentForHybrid = autoSelectTreatmentForHybrid(effectiveHybrid, hybridLookup, brandLookup);
			reconcileTreatmentSelection(
				effectiveHybrid,
				selectedHybridAsProduct,
				selectedProductTreatmentId,
				defaultTreatmentForHybrid,
			);
		}

	}, [series, hybrid, selected, selectedHybrids]);

	const toggleSeriesSelected = (checked: boolean) =>
	{
		// Default Hybrid & Treatment - if we have a selected hybrid and/or selected treatment, use those first
		const defaultHybrid = selectedHybridId ? series.hybrids.find(h => h.HybridId === selectedHybridId) :
			series?.hybrids.filter(h => h.Availability !== 'None' && h.Availability !== 'Unknown')[0];
		const defaultTreatment = selectedTreatmentId ? defaultHybrid.Treatments[selectedTreatmentId] 
			: autoSelectTreatmentForHybrid(defaultHybrid, hybridLookup, brandLookup);
		selectSingleSeries(checked, series.seriesId, selectedHybridId ?? defaultHybrid.HybridId, defaultTreatment.TreatmentName, defaultTreatment.TreatmentId);
	};

	return (
		<SeriesRow className='SeriesContainer' key={series.seriesId + 'FilteredSeriesList'}>
			{!isGoldenHarvestUser && (
				<SeriesAvailability className='SeriesAvailability'>
					{hybrid?.Availability == 'Green' ? <GreenAvailabilityIcon />
						: hybrid?.Availability === 'Yellow' ? <YellowAvailabilityIcon />
							: <RedAvailabilityIcon />
					}
				</SeriesAvailability>
			)}
			<div style={{ display: 'flex', flexDirection: 'column', width: '65%', minWidth: '65%', maxWidth: '65%' }}>
				{
					hybridOptions && hybridOptions.length > 1 ?
						<Dropdown
							variant='outlinednarrow'
							options={hybridOptions}
							onSelect={selectHybrid}
							selected={selectedHybridId}
							className='Hybrid_Dropdown'
							containerWidth={'100%'}
							titleFontSize={theme.fontSizes.extraSmall}
							relativePosition={true}
						/>
						:
						<SeriesData className='HybridName'>
							{series?.hybrids?.find(h => h.HybridId === selectedHybridId)?.HybridName}
						</SeriesData>
				}
				{
					hybrid && hybrid.Treatments && treatmentOptions.length > 1 ?
						<Dropdown
							variant='outlinednarrow'
							options={treatmentOptions}
							onSelect={selectTreatment}
							selected={selectedTreatmentId}
							className='Treatment_Dropdown'
							containerWidth={'100%'}
							titleFontSize={theme.fontSizes.extraSmall}
							relativePosition={true}
							menuStyle={{ width: '130%' }}
						/>
						:
						<SeriesData
							className='TreatmentName'
							style={{ fontWeight: theme.fontWeights.normal, fontSize: theme.fontSizes.tiny }}
						>
							{treatmentOptions[0]?.label}
						</SeriesData>
				}
			</div>
			<div style={{ display: 'flex', flexDirection: 'column', width: '40%', alignItems: 'center' }}>
				{
					hybridTreatmentError &&
					<Popover content={popOverContent} trigger='click'>
						<AlertIcon width={35} height={35} style={{ cursor: 'pointer' }} />
					</Popover>
				}
				<div style={{ display: 'flex', flexDirection: 'row', paddingLeft: 10, paddingRight: 10 }}>
					<div className='SeriesRm' style={{ fontWeight: theme.fontWeights.bold, paddingRight: 2 }}>
						{series.relativeMaturity}
					</div>
					<div style={{ marginLeft: 'auto' }}>
						<Checkbox checked={selected} onChange={e => toggleSeriesSelected(e.target.checked)} />
					</div>
				</div>
			</div>
		</SeriesRow>
	);
};